import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

// Vimeo Player SDK
import Player from "@vimeo/player";

// Toolkit
import { ThemeContext } from "sj-ui-toolkit/src/theme/ThemeContext";

import {
  fetchUserVideoSettings,
  updateUserAttributes,
  updateWatchedVideo,
  VideoSettings,
} from "sj-ui-toolkit/src/api";

import S from "./_styles.module.scss";

interface VideoPlayerProps {
  videoId: string;
  lessonId: number;
  watchedVideo: boolean;
  assessmentId: number;
}

const VideoPlayer = ({ videoId, lessonId, watchedVideo, assessmentId }: VideoPlayerProps) => {
  const { appliedTheme } = useContext(ThemeContext);
  const playerRef = useRef<HTMLDivElement>(null);
  const watchedRef = useRef(watchedVideo);
  const lastPercentRef = useRef(0);
  const progressPercentRef = useRef(0);
  const videoSettingsRef = useRef<VideoSettings | null>(null);
  const volumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // hard-code colors for now
  const colors = useMemo<[string, string, string, string]>(() => {
    return appliedTheme === "atlas-light"
      ? ["eeeef0", "3b82f6", "1e2126", "eeeef0"]
      : ["282d33", "3b82f6", "ffffff", "282d33"];
  }, [appliedTheme]);

  // Small, continuous differences indicate normal playback so we count towards watch progress
  // Big jumps don't count towards progress, but we still update lastPercent so they can start accruing progress again
  // Rewinds are ignored so that can't simply watch the same segments over and over to reach 80%
  const handleTimeUpdate = useCallback(
    (data: { percent: number }) => {
      if (watchedRef.current) return;

      const currentPercent = data.percent;
      const difference = currentPercent - lastPercentRef.current;

      if (difference > 0 && difference < 0.06) {
        lastPercentRef.current = currentPercent;
        progressPercentRef.current += difference;

        if (progressPercentRef.current >= 0.8) {
          watchedRef.current = true;
          // updateWatchedVideo types levelId as required + ids as string, but callers only have
          // the numeric lessonId and the backend tolerates the missing levelId. TODO(T12): loosen.
          updateWatchedVideo({
            lessonId: String(lessonId),
            assessmentId: String(assessmentId),
          } as { levelId: string; lessonId: string; assessmentId: string });
        }
      } else if (difference >= 0.06) {
        lastPercentRef.current = currentPercent;
      }
    },
    [lessonId, assessmentId]
  );

  useEffect(() => {
    if (!videoId || !playerRef.current) return;

    const player = new Player(playerRef.current, {
      id: videoId,
      colors: colors,
    });

    fetchUserVideoSettings().then(settings => {
      if (settings) {
        player.setVolume(settings.volume || 1.0);
        player.setPlaybackRate(settings.playbackRate || 1.0);
        if (settings.textTrack) {
          player.enableTextTrack(settings.textTrack);
        }
      }
      videoSettingsRef.current = settings;
    });

    player.on("volumechange", data => {
      if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
      volumeTimeoutRef.current = setTimeout(() => {
        updateUserAttributes("videoSettings", {
          ...(videoSettingsRef.current ?? {}),
          volume: data.volume,
        });
      }, 250);
    });

    player.on("playbackratechange", data => {
      updateUserAttributes("videoSettings", {
        ...(videoSettingsRef.current ?? {}),
        playbackRate: data.playbackRate,
      });
    });

    player.on("texttrackchange", data => {
      updateUserAttributes("videoSettings", {
        ...(videoSettingsRef.current ?? {}),
        textTrack: data.language,
      });
    });

    if (!watchedRef.current) {
      player.on("timeupdate", data => handleTimeUpdate(data));
    }

    return () => {
      if (volumeTimeoutRef.current) clearTimeout(volumeTimeoutRef.current);
      player.destroy();
    };
  }, [videoId, handleTimeUpdate]);

  return <div className={S.frameContainer} ref={playerRef}></div>;
};

export default VideoPlayer;
